import type { Variants, Transition } from 'motion/react';

/* =========================================================
   Reusable Motion presets — REDESIGN_TZ §4
   Import from `motion/react` (Motion v12, framer-motion successor).
   ========================================================= */

export const easeOutSmooth: Transition = {
  duration: 0.2,
  ease: [0.4, 0, 0.2, 1],
};

export const easeOutExpo: Transition = {
  duration: 0.32,
  ease: [0.16, 1, 0.3, 1],
};

export const springSoft: Transition = {
  type: 'spring',
  damping: 25,
  stiffness: 300,
  mass: 0.6,
};

export const springSnappy: Transition = {
  type: 'spring',
  damping: 22,
  stiffness: 420,
  mass: 0.5,
};

/* ---------------------- Page transitions ---------------------- */

export const pageTransition: Variants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0, transition: easeOutSmooth },
  exit: { opacity: 0, y: -8, transition: easeOutSmooth },
};

/* ---------------------- Fade primitives ---------------------- */

export const fadeIn: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: easeOutSmooth },
};

export const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: easeOutSmooth },
};

export const fadeInDown: Variants = {
  hidden: { opacity: 0, y: -12 },
  show: { opacity: 1, y: 0, transition: easeOutSmooth },
};

export const slideUp: Variants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: easeOutExpo },
};

/* ---------------------- Stagger lists ---------------------- */

export const stagger: Variants = {
  hidden: { opacity: 1 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.04,
      delayChildren: 0.04,
    },
  },
};

export const staggerFast: Variants = {
  hidden: { opacity: 1 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.02,
    },
  },
};

export const staggerItem: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: easeOutSmooth },
};

/* ---------------------- Modals / sheets ---------------------- */

export const modalOverlay: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

export const modalContent: Variants = {
  hidden: { opacity: 0, scale: 0.95, y: 8 },
  show: { opacity: 1, scale: 1, y: 0, transition: springSoft },
  exit: { opacity: 0, scale: 0.97, y: 4, transition: easeOutSmooth },
};

export const sheetSlideRight: Variants = {
  hidden: { x: '100%' },
  show: { x: 0, transition: springSoft },
  exit: { x: '100%', transition: easeOutSmooth },
};

export const sheetSlideLeft: Variants = {
  hidden: { x: '-100%' },
  show: { x: 0, transition: springSoft },
  exit: { x: '-100%', transition: easeOutSmooth },
};

export const sheetSlideBottom: Variants = {
  hidden: { y: '100%' },
  show: { y: 0, transition: springSoft },
  exit: { y: '100%', transition: easeOutSmooth },
};

/* ---------------------- Card / button hover ---------------------- */

export const hoverLift = {
  scale: 1.02,
  y: -2,
  transition: easeOutSmooth,
};

export const hoverShrink = {
  scale: 0.98,
  transition: easeOutSmooth,
};

export const tapShrink = {
  scale: 0.97,
  transition: { duration: 0.1 },
};

/* ---------------------- Notification badge ---------------------- */

export const badgePop: Variants = {
  hidden: { scale: 0, opacity: 0 },
  show: { scale: 1, opacity: 1, transition: { ...springSnappy, delay: 0 } },
  exit: { scale: 0, opacity: 0, transition: { duration: 0.1 } },
};

/* ---------------------- Bubble (chat) ---------------------- */

export const bubbleAppear: Variants = {
  hidden: { opacity: 0, y: 10, scale: 0.98 },
  show: { opacity: 1, y: 0, scale: 1, transition: easeOutSmooth },
};
